import {Circle, Group, Image, Line, Rect, Text} from "react-konva";
import {CharacterSizes} from "@/characters/model/CharacterSizes.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import {DEFAULT_TOKEN_FRAME_STYLE} from "@/encounter/model/TokenFrameStyle.ts";
import {TokenColors, usePortrait} from "@/tabletop/TableTopHooks.ts";
import {useCachedImage} from "@/images/ImageCache.ts";
import frameShadowFile from "@assets/token-frame-shadow.webp";
import crownFile from "@assets/token-crown.svg";
import hoodFile from "@assets/token-hooded-assassin.svg";
import eyeFile from "@assets/token-outside-sight.svg";
import {TOKEN_PORTRAIT_RADIUS_RATIO, tokenFrameImage} from "./TokenFrameStyles.ts";
import {tokenNameplateLayout} from "./TokenNameplate.ts";
import {type AreaEffectMark, visibleAreaEffectMarks} from "@/tabletop/effects/AreaEffectHighlights.ts";

export type RoundTokenAppearanceProps = {
    combatant: Combatant;
    selected?: boolean;
    active?: boolean;
    playerView?: boolean;
    outsidePlayerSight?: boolean;
    areaEffectMarks?: AreaEffectMark[];
};

/** Provider-independent artwork, shared by tabletop rendering and the isolated design preview. */
export function RoundTokenAppearance({combatant, selected = false, active = false, playerView = false,
    outsidePlayerSight = false, areaEffectMarks = []}: RoundTokenAppearanceProps) {
    const size = CharacterSizes.get(combatant.character.size)!;
    const frameRadius = size / 2;
    const portraitRadius = frameRadius * TOKEN_PORTRAIT_RADIUS_RATIO;
    // Scale ornaments with the creature's world size, rather than capping large tokens' icons.
    const badgeRadius = size * 0.095;
    const badgeStrokeWidth = size * 0.01;
    const crownWidth = size * 0.245;
    const crownHeight = crownWidth * 0.86;
    const badgeX = frameRadius * 0.63;
    const badgeY = -frameRadius * 0.69;
    const nameplate = tokenNameplateLayout(combatant.name, size);
    const portrait = usePortrait(portraitRadius * 2, combatant.character.image);
    const frame = useCachedImage(tokenFrameImage(combatant.tokenFrame ?? DEFAULT_TOKEN_FRAME_STYLE));
    const frameShadow = useCachedImage(frameShadowFile);
    const crown = useCachedImage(crownFile);
    const hood = useCachedImage(hoodFile);
    const eye = useCachedImage(eyeFile);
    const factionColor = combatant.hostile ? TokenColors.hostile.light : TokenColors.friendly.light;
    const health = combatant.maxLife > 0 && Number.isFinite(combatant.currentLife)
        ? Math.max(0, Math.min(1, combatant.currentLife / combatant.maxLife)) : 1;
    const marks = visibleAreaEffectMarks(areaEffectMarks);
    const markSize = size * (16 / 150);
    const markGap = size / 30;
    const markY = nameplate.top + nameplate.height + markGap;

    if (combatant.hidden && playerView) return null;

    return <Group>
        {/* Local selection: a blue ring outside the token frame, visible only to the GM. */}
        {selected && !playerView && <Circle name="local-selection" radius={frameRadius}
            stroke="#38bdf8" strokeWidth={Math.max(3, size * 0.02)}
            shadowColor="#38bdf8" shadowBlur={Math.max(3, size * 0.02)} listening={false}/>}
        {/* Facing direction: a faction-colored pointer rotates independently of the portrait. */}
        <Group rotation={combatant.rotation} listening={false}>
            <Line points={[frameRadius - 5, -size * 0.08, frameRadius + size * 0.1, 0,
                frameRadius - 5, size * 0.08]} closed fill={factionColor} stroke="#111827"
                strokeWidth={Math.max(1, size * 0.015)}/>
        </Group>
        {/* Opaque portrait base: the hit target for selecting and dragging the token. */}
        <Circle radius={portraitRadius} fill="#ffffff" name="Token" id={combatant.id}/>
        {/* Character portrait: clip the image and health overlay to the inner frame circle. */}
        <Group listening={false} clipFunc={context => context.arc(0, 0, portraitRadius, 0, Math.PI * 2)}>
            {portrait && <Image image={portrait.image} x={-portrait.width / 2} y={-portrait.height / 2}
                width={portrait.width} height={portrait.height}/>}
            {/* Lost hit points: a translucent red fill rises from the bottom of the portrait. */}
            {health < 1 && <Rect x={-portraitRadius} y={-portraitRadius + 2 * portraitRadius * health}
                width={portraitRadius * 2} height={portraitRadius * 2 * (1 - health)} fill="#640000" opacity={0.6}/>}
        </Group>
        {/* Inner frame shadow: visually recesses the portrait beneath the frame artwork. */}
        <Image image={frameShadow} x={-frameRadius} y={-frameRadius} width={size} height={size} listening={false}/>
        {/* Token frame: decorative artwork never intercepts pointer interactions. */}
        <Image image={frame} x={-frameRadius} y={-frameRadius} width={size} height={size} listening={false}/>
        {/* Outside player sight: darken the portrait and frame while leaving status markers legible. */}
        {!playerView && outsidePlayerSight && <Circle name="outside-sight-dimming" radius={frameRadius}
            fill="#020617" opacity={0.3} listening={false}/>}
        {/* Active turn: the gold crown sits in front of the frame at the top center. */}
        {active && <Image name="active-turn-crown" image={crown} x={-crownWidth / 2}
            y={-frameRadius * 0.89 - crownHeight / 2} width={crownWidth} height={crownHeight}
            shadowColor="#000000" shadowBlur={size * 0.02} shadowOffsetY={size / 75} listening={false}/>}
        {/* Hidden from players: a hooded figure occupies the upper-left foreground slot. */}
        {!playerView && combatant.hidden && <Group name="hidden-status" x={-badgeX} y={badgeY} listening={false}>
            <Circle radius={badgeRadius} fill="#101823" stroke="#8795a6" strokeWidth={badgeStrokeWidth}/>
            <Image image={hood} x={-badgeRadius * 0.78} y={-badgeRadius * 0.78}
                width={badgeRadius * 1.56} height={badgeRadius * 1.56}/>
        </Group>}
        {/* Outside player sight: the eye occupies the upper-right slot without overlapping the crown or hood. */}
        {!playerView && outsidePlayerSight && <Group name="outside-sight-status" x={badgeX} y={badgeY} listening={false}>
            <Circle radius={badgeRadius} fill="#101823" stroke="#b6a2db" strokeWidth={badgeStrokeWidth}/>
            <Image image={eye} x={-badgeRadius * 0.65} y={-badgeRadius * 0.65}
                width={badgeRadius * 1.3} height={badgeRadius * 1.3}/>
        </Group>}
        {/* Name plate: one or two text lines overlap the lower inner edge of the frame. */}
        <Group y={nameplate.top} listening={false}>
            <Rect x={-nameplate.width / 2} width={nameplate.width} height={nameplate.height}
                cornerRadius={6} fill="#080c14" opacity={0.85}/>
            <Text x={-nameplate.width / 2 + nameplate.paddingX} y={nameplate.paddingY}
                width={nameplate.width - 2 * nameplate.paddingX} text={nameplate.text}
                fontSize={nameplate.fontSize} lineHeight={nameplate.lineHeight} wrap="none"
                fontFamily={nameplate.fontFamily} fontStyle="bold" fill="white" align="center"/>
        </Group>
        {/* Highlighted areas: numbered color badges form a centered row beneath the name plate. */}
        {marks.map((mark, index) => <Group key={mark.id} name={`area-effect-mark-${mark.id}`}
            x={(index - (marks.length - 1) / 2) * (markSize + markGap)} y={markY} listening={false}>
            <Rect x={-markSize / 2} width={markSize} height={markSize} cornerRadius={3}
                fill={mark.color} stroke="#111827" strokeWidth={size / 150}/>
            <Text x={-markSize / 2} width={markSize} height={markSize} text={mark.label}
                align="center" verticalAlign="middle" fontSize={markSize * (mark.label.length > 2 ? 0.48 : 0.65)}
                fontStyle="bold" fill="white" stroke="#111827" strokeWidth={size * (0.7 / 150)} fillAfterStrokeEnabled/>
        </Group>)}
    </Group>;
}
